import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, UserPlus, Plus } from "@phosphor-icons/react";
import CrudActions from "@/components/CrudActions";
import { toast } from "sonner";

export default function Fleet() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [types, setTypes] = useState([]);
  const [vOpen, setVOpen] = useState(false);
  const [dOpen, setDOpen] = useState(false);
  const [vForm, setVForm] = useState({ reg_number: "", vehicle_type: "diesel_truck_heavy", capacity_tons: 25 });
  const [dForm, setDForm] = useState({ name: "", email: "", password: "", phone: "" });

  const load = () => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data));
  };
  useEffect(() => { load(); api.get("/vehicle-types").then((r) => setTypes(r.data)); }, []);

  const addVehicle = async () => {
    try { await api.post("/vehicles", { ...vForm, capacity_tons: Number(vForm.capacity_tons) }); toast.success("Vehicle added"); setVOpen(false); setVForm({ reg_number: "", vehicle_type: "diesel_truck_heavy", capacity_tons: 25 }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addDriver = async () => {
    try { await api.post("/drivers", dForm); toast.success("Driver added"); setDOpen(false); setDForm({ name: "", email: "", password: "", phone: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [editV, setEditV] = useState(null);
  const [editD, setEditD] = useState(null);

  const saveVehicle = async () => {
    try {
      await api.put(`/vehicles/${editV.id}`, { reg_number: editV.reg_number, vehicle_type: editV.vehicle_type, capacity_tons: Number(editV.capacity_tons) });
      toast.success("Vehicle updated"); setEditV(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delVehicle = async (id) => {
    try { await api.delete(`/vehicles/${id}`); toast.success("Vehicle deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveDriver = async () => {
    try { await api.put(`/drivers/${editD.id}`, { name: editD.name, phone: editD.phone }); toast.success("Driver updated"); setEditD(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delDriver = async (id) => {
    try { await api.delete(`/drivers/${id}`); toast.success("Driver deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Layout title="Fleet & Drivers" subtitle="Manage vehicles and driver accounts">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* vehicles */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-heading font-semibold flex items-center gap-2"><Truck size={18} weight="duotone" /> Vehicles</h3>
            <Dialog open={vOpen} onOpenChange={setVOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-vehicle-btn" className="bg-slate-900 hover:bg-slate-800"><Plus size={14} className="mr-1" />Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Add Vehicle</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Registration</Label><Input data-testid="v-reg" className="mt-1.5" value={vForm.reg_number} onChange={(e) => setVForm({ ...vForm, reg_number: e.target.value })} placeholder="MH01AB0000" /></div>
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Type</Label>
                    <Select value={vForm.vehicle_type} onValueChange={(v) => setVForm({ ...vForm, vehicle_type: v })}>
                      <SelectTrigger data-testid="v-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{types.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`v-type-${t.value}`}>{t.label} · {t.emission_factor} kg/km</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Capacity (tons)</Label><Input data-testid="v-cap" type="number" className="mt-1.5" value={vForm.capacity_tons} onChange={(e) => setVForm({ ...vForm, capacity_tons: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="submit-vehicle" onClick={addVehicle} className="bg-slate-900 hover:bg-slate-800">Add Vehicle</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-slate-100">
            {vehicles.map((v) => (
              <div key={v.id} data-testid={`vehicle-${v.reg_number}`} className="px-5 py-3 flex items-center justify-between">
                <div><div className="font-mono font-medium text-slate-900">{v.reg_number}</div><div className="text-xs text-slate-500">{v.vehicle_type_label}</div></div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-medium">{v.capacity_tons} t</div>
                  <CrudActions testidBase={`vehicle-${v.reg_number}`} name="vehicle" canDelete={!v.linked}
                    linkedHint="Assigned to shipments — cannot delete" onEdit={() => setEditV({ ...v })} onDelete={() => delVehicle(v.id)} />
                </div>
              </div>
            ))}
            {vehicles.length === 0 && <div className="px-5 py-8 text-sm text-slate-400 text-center">No vehicles yet.</div>}
          </div>
        </div>

        {/* drivers */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-heading font-semibold flex items-center gap-2"><UserPlus size={18} weight="duotone" /> Drivers</h3>
            <Dialog open={dOpen} onOpenChange={setDOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-driver-btn" className="bg-slate-900 hover:bg-slate-800"><Plus size={14} className="mr-1" />Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Add Driver</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Name</Label><Input data-testid="d-name" className="mt-1.5" value={dForm.name} onChange={(e) => setDForm({ ...dForm, name: e.target.value })} /></div>
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Email</Label><Input data-testid="d-email" type="email" className="mt-1.5" value={dForm.email} onChange={(e) => setDForm({ ...dForm, email: e.target.value })} /></div>
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Password</Label><Input data-testid="d-pw" type="password" className="mt-1.5" value={dForm.password} onChange={(e) => setDForm({ ...dForm, password: e.target.value })} /></div>
                  <div><Label className="text-xs uppercase font-bold text-slate-500">Phone</Label><Input data-testid="d-phone" className="mt-1.5" value={dForm.phone} onChange={(e) => setDForm({ ...dForm, phone: e.target.value })} /></div>
                </div>
                <DialogFooter><Button data-testid="submit-driver" onClick={addDriver} className="bg-slate-900 hover:bg-slate-800">Add Driver</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-slate-100">
            {drivers.map((d) => (
              <div key={d.id} data-testid={`driver-${d.email}`} className="px-5 py-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-semibold">{d.name.charAt(0)}</div>
                <div className="flex-1"><div className="text-sm font-medium text-slate-900">{d.name}</div><div className="text-xs text-slate-500">{d.email}</div></div>
                <CrudActions testidBase={`driver-${d.email}`} name="driver" canDelete={!d.linked}
                  linkedHint="Assigned to shipments — cannot delete" onEdit={() => setEditD({ ...d })} onDelete={() => delDriver(d.id)} />
              </div>
            ))}
            {drivers.length === 0 && <div className="px-5 py-8 text-sm text-slate-400 text-center">No drivers yet.</div>}
          </div>
        </div>
      </div>

      {editV && (
        <Dialog open={!!editV} onOpenChange={(o) => !o && setEditV(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Edit Vehicle</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-xs uppercase font-bold text-slate-500">Registration</Label><Input data-testid="edit-v-reg" className="mt-1.5" value={editV.reg_number} onChange={(e) => setEditV({ ...editV, reg_number: e.target.value })} /></div>
              <div><Label className="text-xs uppercase font-bold text-slate-500">Type</Label>
                <Select value={editV.vehicle_type} onValueChange={(v) => setEditV({ ...editV, vehicle_type: v })}>
                  <SelectTrigger data-testid="edit-v-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`edit-v-type-${t.value}`}>{t.label} · {t.emission_factor} kg/km</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase font-bold text-slate-500">Capacity (tons)</Label><Input data-testid="edit-v-cap" type="number" className="mt-1.5" value={editV.capacity_tons} onChange={(e) => setEditV({ ...editV, capacity_tons: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="save-vehicle" onClick={saveVehicle} className="bg-slate-900 hover:bg-slate-800">Save Changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {editD && (
        <Dialog open={!!editD} onOpenChange={(o) => !o && setEditD(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Edit Driver</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-xs uppercase font-bold text-slate-500">Name</Label><Input data-testid="edit-d-name" className="mt-1.5" value={editD.name} onChange={(e) => setEditD({ ...editD, name: e.target.value })} /></div>
              <div><Label className="text-xs uppercase font-bold text-slate-500">Email</Label><Input className="mt-1.5 bg-slate-50" value={editD.email} disabled /></div>
              <div><Label className="text-xs uppercase font-bold text-slate-500">Phone</Label><Input data-testid="edit-d-phone" className="mt-1.5" value={editD.phone || ""} onChange={(e) => setEditD({ ...editD, phone: e.target.value })} /></div>
            </div>
            <DialogFooter><Button data-testid="save-driver" onClick={saveDriver} className="bg-slate-900 hover:bg-slate-800">Save Changes</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}
